/**
 * @file invoiceProposals.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-08
 * @description The invoice changes a supplied-quantity change calls for
 *              (Cam, 8 Oct 2026; server: services/invoiceSupplyService). A
 *              save that changes a supplied quantity on an order whose
 *              invoice was raised and has not gone out is answered 409
 *              INVOICE_CHANGES_PROPOSED { proposals }; the screen shows them
 *              (InvoiceProposalDialog) and, on "Apply these changes and
 *              save", sends the same save again with `invoice_changes`. The
 *              answer's `invoice_update` says what happened to the invoice.
 */

/** Is this response the server's invoice question? */
export const isInvoiceQuestion = (status, body) => status === 409 && body?.code === 'INVOICE_CHANGES_PROPOSED';

/** The answer "apply", carrying each invoice's lines as the user was shown them. */
export function applyAnswer(proposals) {
  return {
    apply: true,
    expected: (proposals || []).map(p => ({ invoice_id: p.invoice_id, lines: p.expected || [] }))
  };
}

/**
 * What happened to the invoice after a save, in words — null when nothing
 * was asked. A refusal says the invoice was NOT changed, and where to do it.
 * @param {object|null} update — the response's `invoice_update`
 * @param {{ where?: string }} [opts] — where the changes can be offered again
 */
export function invoiceUpdateMessage(update, { where = null } = {}) {
  if (!update) return null;
  const parts = [];
  if (update.message) parts.push(update.message);
  if (update.refused && update.refused.length) {
    parts.push(where
      ? `The changes are still offered from ${where}, or edit the invoice yourself.`
      : 'Edit the invoice to match the new supplied quantity.');
  }
  return parts.join(' ') || null;
}

/** The invoices a refused or left update names, for "Open the invoice" buttons. */
export function invoicesToOpen(update) {
  if (!update) return [];
  const list = [...(update.refused || []), ...(update.left || [])];
  const seen = new Set();
  return list
    .map(r => ({ id: r.invoice_id, number: r.invoice_no }))
    .filter(r => r.id && !seen.has(r.id) && seen.add(r.id));
}

/** "£1,234.50" for the proposal window. */
export const money = (n) => `£${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Open the invoice editor on one invoice (the Search Invoices "Edit" route:
 * sessionStorage 'editInvoiceId', then the Create Invoice screen).
 * @param {number} invoiceId
 * @param {(menu: string) => void} navigate
 */
export function openInvoiceEditor(invoiceId, navigate) {
  if (!invoiceId || typeof navigate !== 'function') return false;
  try { sessionStorage.setItem('editInvoiceId', String(invoiceId)); } catch (_) { return false; }
  navigate('create-invoice');
  return true;
}
